import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Footer from "../components/Footer";
import { errMsg } from "../lib/errors";
import { LogOut } from "lucide-react";

// Professional self-attestation — KISS (2026-08).
//
// One screen after the doctor's first sign-in. Collects full name,
// profession (enum), primary licensing state/territory (enum),
// license number (free text — letters, hyphens, and leading zeros
// preserved verbatim), and one accuracy checkbox. No verification,
// NPI, board search, upload, badge, or credentialing.
//
// Server-side we store all four values but only self_attested_at is
// echoed through /auth/me — the profession, state, and license
// number are never returned to the client after write.
//
// Safety guarantees (no user is ever trapped by a failed submit):
//   - Errors keep every field value intact and re-enable Submit.
//   - Visible Sign-out button in the card header.
//   - Visible "Return to landing" link at the bottom.
const PROFESSIONS = [
  "Dentist",
  "Dental Specialist",
  "Other Licensed Dental Professional",
];

// US states + DC + 5 inhabited territories. Kept alphabetical.
const STATES = [
  "Alabama", "Alaska", "American Samoa", "Arizona", "Arkansas",
  "California", "Colorado", "Connecticut", "Delaware",
  "District of Columbia", "Florida", "Georgia", "Guam", "Hawaii",
  "Idaho", "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky",
  "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan",
  "Minnesota", "Mississippi", "Missouri", "Montana", "Nebraska",
  "Nevada", "New Hampshire", "New Jersey", "New Mexico", "New York",
  "North Carolina", "North Dakota", "Northern Mariana Islands",
  "Ohio", "Oklahoma", "Oregon", "Pennsylvania", "Puerto Rico",
  "Rhode Island", "South Carolina", "South Dakota", "Tennessee",
  "Texas", "Utah", "Vermont", "Virgin Islands", "Virginia",
  "Washington", "West Virginia", "Wisconsin", "Wyoming",
];

export default function SelfAttestation() {
  const { user, refresh, logout } = useAuth();
  const nav = useNavigate();

  const [fullName, setFullName] = useState(user?.full_name || "");
  const [profession, setProfession] = useState("");
  const [licenseState, setLicenseState] = useState("");
  const [licenseNumber, setLicenseNumber] = useState("");
  const [accurate, setAccurate] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const canSubmit =
    !!fullName.trim() &&
    !!profession &&
    !!licenseState &&
    !!licenseNumber.trim() &&
    accurate &&
    !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");
    try {
      await api.patch("/auth/self-attestation", {
        full_name: fullName.trim(),
        profession,
        license_state: licenseState,
        // License number preserved verbatim (only trim whitespace).
        license_number: licenseNumber.trim(),
        attest_accurate: true,
      });
      await refresh();
      nav("/dashboard", { replace: true });
    } catch (err) {
      setError(errMsg(err, "Could not record your attestation. Please try again."));
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[var(--pa-bg,#0a0a0a)] text-[var(--pa-text,#fff)] flex flex-col"
      data-testid="self-attestation-page"
    >
      <header className="border-b border-[var(--pa-border,#333)]">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <span className="font-serif text-lg text-[var(--pa-gold,#d4af37)]">
            ProsthoAI
          </span>
          <button
            type="button"
            onClick={() => {
              logout && logout();
              nav("/", { replace: true });
            }}
            data-testid="self-attestation-signout"
            className="inline-flex items-center gap-1.5 text-xs text-[var(--pa-text-muted,#aaa)] hover:text-[var(--pa-gold,#d4af37)] transition"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </header>

      <main className="flex-1 flex items-start justify-center px-4 sm:px-6 py-10">
        <div
          className="w-full max-w-xl bg-[var(--pa-surface,#111)] border border-[var(--pa-border,#333)] rounded-lg p-6 sm:p-8"
          data-testid="self-attestation-form"
        >
          <h1
            className="font-serif text-2xl text-[var(--pa-text,#fff)]"
            data-testid="self-attestation-heading"
          >
            Professional self-attestation
          </h1>
          <p className="mt-2 text-sm text-[var(--pa-text-muted,#aaa)] leading-relaxed">
            Before you continue, please confirm your professional information.
            This is a self-attestation only — we do not verify these fields,
            run NPI or board searches, or ask for documents.
          </p>

          <div className="mt-6 space-y-4">
            <label className="block">
              <span className="block text-xs uppercase tracking-widest text-[var(--pa-text-muted,#888)] mb-1">
                Full name
              </span>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                maxLength={200}
                data-testid="self-attestation-full-name"
                className="w-full bg-[var(--pa-bg-2,#0b0b0b)] border border-[var(--pa-border,#333)] rounded-md text-[var(--pa-text,#fff)] text-sm p-2.5 focus:outline-none focus:border-[var(--pa-gold,#d4af37)]"
              />
            </label>
            <label className="block">
              <span className="block text-xs uppercase tracking-widest text-[var(--pa-text-muted,#888)] mb-1">
                Profession
              </span>
              <select
                value={profession}
                onChange={(e) => setProfession(e.target.value)}
                data-testid="self-attestation-profession"
                className="w-full bg-[var(--pa-bg-2,#0b0b0b)] border border-[var(--pa-border,#333)] rounded-md text-[var(--pa-text,#fff)] text-sm p-2.5 focus:outline-none focus:border-[var(--pa-gold,#d4af37)]"
              >
                <option value="">Select…</option>
                {PROFESSIONS.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="block text-xs uppercase tracking-widest text-[var(--pa-text-muted,#888)] mb-1">
                Primary licensing state / territory
              </span>
              <select
                value={licenseState}
                onChange={(e) => setLicenseState(e.target.value)}
                data-testid="self-attestation-license-state"
                className="w-full bg-[var(--pa-bg-2,#0b0b0b)] border border-[var(--pa-border,#333)] rounded-md text-[var(--pa-text,#fff)] text-sm p-2.5 focus:outline-none focus:border-[var(--pa-gold,#d4af37)]"
              >
                <option value="">Select…</option>
                {STATES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="block text-xs uppercase tracking-widest text-[var(--pa-text-muted,#888)] mb-1">
                License number
              </span>
              <input
                type="text"
                value={licenseNumber}
                onChange={(e) => setLicenseNumber(e.target.value)}
                maxLength={80}
                autoComplete="off"
                spellCheck={false}
                data-testid="self-attestation-license-number"
                className="w-full bg-[var(--pa-bg-2,#0b0b0b)] border border-[var(--pa-border,#333)] rounded-md text-[var(--pa-text,#fff)] text-sm p-2.5 focus:outline-none focus:border-[var(--pa-gold,#d4af37)] font-mono"
              />
              <span className="mt-1 block text-[10px] text-[var(--pa-text-dim,#666)]">
                Letters, hyphens, and leading zeros are preserved.
              </span>
            </label>
          </div>

          <label className="mt-6 flex items-start gap-2.5 text-sm text-[var(--pa-text,#eee)] cursor-pointer">
            <input
              type="checkbox"
              checked={accurate}
              onChange={(e) => setAccurate(e.target.checked)}
              data-testid="self-attestation-accurate-checkbox"
              className="mt-0.5 h-4 w-4 accent-[var(--pa-gold,#d4af37)]"
            />
            <span>I confirm the information above is accurate.</span>
          </label>

          {error && (
            <div
              role="alert"
              data-testid="self-attestation-error"
              className="mt-4 rounded-md border border-red-500/40 bg-red-500/10 text-red-300 text-sm p-3"
            >
              <div>{error}</div>
              <button
                type="button"
                onClick={submit}
                data-testid="self-attestation-try-again"
                className="mt-2 text-xs text-red-200 underline underline-offset-2 hover:text-red-100"
              >
                Try again
              </button>
            </div>
          )}

          <div className="mt-6 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={submit}
              disabled={!canSubmit}
              data-testid="self-attestation-submit"
              className="inline-flex items-center gap-1.5 bg-[var(--pa-gold,#d4af37)] text-[var(--pa-bg,#0a0a0a)] font-semibold px-5 py-2.5 rounded-md hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              {submitting ? "Submitting…" : "Submit and continue"}
            </button>
          </div>

          <p className="mt-6 text-[11px] text-[var(--pa-text-dim,#666)] leading-relaxed">
            No verification, board search, NPI lookup, document upload, or
            verified badge is performed. Your license number is stored
            confidentially and is not returned in subsequent authentication
            responses.
          </p>

          <p className="mt-4 text-[11px] text-[var(--pa-text-muted,#888)]">
            Prefer to leave without submitting?{" "}
            <Link
              to="/"
              data-testid="self-attestation-return-home"
              className="text-[var(--pa-gold,#d4af37)] hover:underline"
            >
              Return to landing page
            </Link>
            .
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
}
